import React, { useCallback, useContext, useState } from "react";
import EmployeeActionsContext from "./context";
import CompanyContext from "../../data/CompanyWrapper/context";
import { uploadEmployeesV2 } from "../../../../../usecases/employees/upload-employees";
import ModalContext from "../../ModalWrapper/context";
import EmployeesContext from "../../data/EmployeesWrapper/context";
import generateUploadEmployeesSpreadsheetTemplate from "../../../../../services/generateUploadEmployeesSpreadsheetTemplate";
import { getCsvEmployees } from "../../../../../usecases/employees/get-csv-employees";
import { saveEmployeesV2 } from "../../../../../usecases/employees/save-employee";
import { removeUninvitedEmployeesById } from "../../../../../usecases/employees/remove-employees-bulk-session";
import changeEmployeesBenefitPlan from "../../../../../usecases/employees/change-employees-benefit-plan";
import changeEmployeesTaxRegime from "../../../../../usecases/employees/change-employees-tax-regime";
import { inviteUploadingEmployeesV2 } from "../../../../../usecases/employees/invite-uploading-employees";
import BenefitPlansContext from "../../data/BenefitPlanWrapper/context";
import { translate as t } from "../../../../translate/localization";
import DeliverBagToEmployeesModal from "../../../../renders/DeliverBagToEmployeesModal";
import BenefitPlan from "../../../../../app/domain/BenefitPlan";
import { MonetaryAmount } from "../../../../../app/domain/monetary-amount";
import Employee from "../../../../../app/domain/Employee";
import addEmployeesToExistantModelWithBag from "../../../../../usecases/benefit-model/addEmployeesToExistantModelWithBag";
import { showErrorMessage } from "../../../../../usecases/commons/show-error-message";

export default function EmployeeActionWrapper(props: any) {
  const { company } = useContext(CompanyContext);
  const { loadEmployees } = useContext(EmployeesContext);
  const { closeModal, openModal } = useContext(ModalContext);
  const { benefitPlans } = useContext(BenefitPlansContext);

  const [createNewEmployeeIsLoading, setCreateNewEmployeesIsLoading] =
    useState(false);
  const createNewEmployee = useCallback(
    async ({ employee, benefitPlanId, taxRegime }): Promise<void> => {
      setCreateNewEmployeesIsLoading(true);
      await uploadEmployeesV2({
        employees: [employee],
        benefitPlanId,
        taxRegime,
        company,
      });
      setCreateNewEmployeesIsLoading(false);
      closeModal();
      await loadEmployees();
    },
    [closeModal, company, loadEmployees]
  );

  const [editEmployeeDetailsIsLoading, setEditEmployeeDetailsIsLoading] =
    useState(false);
  const editEmployeeDetails = useCallback(
    async ({ employees, benefitPlan, taxRegime }): Promise<void> => {
      setEditEmployeeDetailsIsLoading(true);
      await saveEmployeesV2({
        companyId: company.id,
        employees,
        benefitPlan,
        taxRegime,
      });
      setEditEmployeeDetailsIsLoading(false);
      closeModal();
      await loadEmployees();
    },
    [closeModal, company.id, loadEmployees]
  );

  const downloadEmployeeTemplate = useCallback(async () => {
    generateUploadEmployeesSpreadsheetTemplate();
  }, []);

  const [
    uploadMultipleEmployeesIsLoading,
    setUploadMultipleEmployeesIsLoading,
  ] = useState(false);
  const uploadMultipleEmployees = useCallback(
    async ({ filename }): Promise<void> => {
      setUploadMultipleEmployeesIsLoading(true);
      await getCsvEmployees({
        type: "massiveUpload",
        filename,
        company,
      } as never);
      setUploadMultipleEmployeesIsLoading(false);
      closeModal();
      await loadEmployees();
    },
    [company, loadEmployees, closeModal]
  );

  const [deleteEmployeesByIdIsLoading, setDeleteEmployeesByIdIsLoading] =
    useState(false);
  const deleteEmployeesById = useCallback(
    async ({ employeeIds, sideEffect }): Promise<void> => {
      setDeleteEmployeesByIdIsLoading(true);
      closeModal();
      await removeUninvitedEmployeesById({
        companyId: company.id,
        employeeIds,
      });
      setDeleteEmployeesByIdIsLoading(false);
      sideEffect();
      await loadEmployees();
    },
    [closeModal, company, loadEmployees]
  );

  const [
    modifyEmployeesBenefitPlanIsLoading,
    setModifyEmployeesBenefitPlanIsLoading,
  ] = useState(false);
  const modifyEmployeesBenefitPlan = useCallback(
    async ({ employeeIds, benefitPlanId }): Promise<void> => {
      setModifyEmployeesBenefitPlanIsLoading(true);
      await changeEmployeesBenefitPlan({
        companyId: company.id,
        employeeIds,
        benefitPlanId,
      });
      setModifyEmployeesBenefitPlanIsLoading(false);
      await loadEmployees();
    },
    [loadEmployees, company]
  );

  const [
    modifyEmployeesTaxRegimeIsLoading,
    setModifyEmployeesTaxRegimeIsLoading,
  ] = useState(false);
  const modifyEmployeesTaxRegime = useCallback(
    async ({ employeeIds, taxRegime }): Promise<void> => {
      setModifyEmployeesTaxRegimeIsLoading(true);
      await changeEmployeesTaxRegime({
        companyId: company.id,
        employeeIds,
        taxRegime,
      });
      setModifyEmployeesTaxRegimeIsLoading(false);
      await loadEmployees();
    },
    [loadEmployees, company]
  );

  const [deliverBagToEmployeesIsLoading, setDeliverBagToEmployeesIsLoading] =
    useState(false);
  const deliverBagToEmployees = useCallback(
    async ({
      amount,
      date,
      employees,
      benefitPlan,
    }: {
      amount: MonetaryAmount;
      date: Date;
      employees: Employee[];
      benefitPlan: BenefitPlan;
    }): Promise<void> => {
      setDeliverBagToEmployeesIsLoading(true);
      await addEmployeesToExistantModelWithBag({
        company,
        benefitPlan,
        amount,
        date,
        employees,
      });
      setDeliverBagToEmployeesIsLoading(false);
      closeModal();
      await loadEmployees();
    },
    [closeModal, company, loadEmployees]
  );

  const [
    inviteUploadingEmployeesIsLoading,
    setInviteUploadingEmployeesIsLoading,
  ] = useState(false);
  const inviteUploadingEmployees = useCallback(
    async ({ employees, benefitPlanId, sideEffect }): Promise<void> => {
      setInviteUploadingEmployeesIsLoading(true);
      const benefitPlan =
        benefitPlans.find(({ id }) => id === benefitPlanId) ||
        new BenefitPlan({});
      await inviteUploadingEmployeesV2({
        company,
        employees,
        benefitPlan,
        onInviteSuccessfully: async () => {
          await loadEmployees();
        },
        addEmployeesToBenefitPlanWithBag: ({ inviteEmployeesHandler }) => {
          openModal({
            title: t("benefits-section.onboarding.what-is-a-benefit-plan"),
            modalContents: (
              <DeliverBagToEmployeesModal
                withoutWrapper
                onConfirm={async ({ amount, date }) => {
                  setInviteUploadingEmployeesIsLoading(true);
                  const invitedEmployees = await inviteEmployeesHandler();
                  if (invitedEmployees.length > 0) {
                    await deliverBagToEmployees({
                      amount,
                      date,
                      employees: invitedEmployees,
                      benefitPlan,
                    });
                  }

                  closeModal();
                  setInviteUploadingEmployeesIsLoading(false);
                }}
                periodDurationInPayrolls={benefitPlan.benefitBag.periodicity}
                initialAmount={MonetaryAmount.fromUnitary(
                  benefitPlan.benefitBag.amount
                )}
              />
            ),
          });
        },
      });
      if (sideEffect) {
        sideEffect();
      }
      setInviteUploadingEmployeesIsLoading(false);
    },
    [
      benefitPlans,
      company,
      deliverBagToEmployees,
      loadEmployees,
      openModal,
      closeModal,
    ]
  );

  return (
    <EmployeeActionsContext.Provider
      value={{
        createNewEmployeeIsLoading,
        createNewEmployee,

        editEmployeeDetailsIsLoading,
        editEmployeeDetails,

        downloadEmployeeTemplate,

        uploadMultipleEmployeesIsLoading,
        uploadMultipleEmployees,

        deliverBagToEmployeesIsLoading,

        deleteEmployeesByIdIsLoading,
        deleteEmployeesById,

        modifyEmployeesBenefitPlanIsLoading,
        modifyEmployeesBenefitPlan,

        modifyEmployeesTaxRegimeIsLoading,
        modifyEmployeesTaxRegime,

        inviteUploadingEmployeesIsLoading,
        inviteUploadingEmployees,
      }}
      {...props}
    />
  );
}
